import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useClinic } from '@/contexts/ClinicContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { ChevronLeft, ChevronRight, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import {
  partnerFailureService,
  PartnerFailure,
  PARTNER_FAILURE_CATEGORY_LABELS,
  PARTNER_FAILURE_RELATED_ENTITY,
  PARTNER_FAILURE_RETRYABLE,
} from '@/services/partnerFailureService';

const PAGE_SIZE = 20;

type CategoryFilter = 'all' | keyof typeof PARTNER_FAILURE_CATEGORY_LABELS;
type ResolvedFilter = 'unresolved' | 'resolved';

function formatDate(iso: string) {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function DetailGrid({ children }: { children: React.ReactNode }) {
  return <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">{children}</dl>;
}

function DetailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={mono ? 'break-all font-mono text-xs' : 'text-sm'}>{value}</dd>
    </div>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  let text: string;
  try {
    text = JSON.stringify(value, null, 2);
  } catch {
    text = String(value);
  }
  return (
    <pre className="max-h-80 overflow-auto rounded-md border bg-muted/40 p-3 font-mono text-xs">
      {text}
    </pre>
  );
}

function relatedRecordLink(failure: PartnerFailure): { label: string; to: string } | null {
  const related = PARTNER_FAILURE_RELATED_ENTITY[failure.category as keyof typeof PARTNER_FAILURE_RELATED_ENTITY];
  if (related === 'prescription' && failure.prescription_id) {
    return { label: 'Prescription', to: `/prescriptions/${failure.prescription_id}` };
  }
  if (related === 'patient' && failure.patient_id) {
    return { label: 'Patient', to: `/patients/${failure.patient_id}` };
  }
  if (related === 'appointment' && failure.appointment_id) {
    return { label: 'Appointment', to: `/emr/calendar?appointment=${failure.appointment_id}` };
  }
  return null;
}

const PartnerFailures = () => {
  const { clinic } = useClinic();
  const clinicId = clinic?.id;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [resolvedFilter, setResolvedFilter] = useState<ResolvedFilter>('unresolved');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [page, setPage] = useState(1);
  const [resolveTarget, setResolveTarget] = useState<PartnerFailure | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [detailTarget, setDetailTarget] = useState<PartnerFailure | null>(null);

  const listQueryKey = useMemo(
    () => ['partnerFailures', clinicId, resolvedFilter, categoryFilter, page],
    [clinicId, resolvedFilter, categoryFilter, page],
  );

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: listQueryKey,
    queryFn: async () => {
      if (!clinicId) return null;
      return partnerFailureService.list(clinicId, {
        page,
        limit: PAGE_SIZE,
        resolved: resolvedFilter === 'resolved',
        category: categoryFilter === 'all' ? undefined : categoryFilter,
      });
    },
    enabled: Boolean(clinicId),
  });

  const { data: countsData } = useQuery({
    queryKey: ['partnerFailureCounts', clinicId, resolvedFilter],
    queryFn: async () => {
      if (!clinicId) return null;
      return partnerFailureService.counts(clinicId, resolvedFilter === 'resolved');
    },
    enabled: Boolean(clinicId),
  });

  const resolveMutation = useMutation({
    mutationFn: async (vars: { id: string; note?: string }) => {
      if (!clinicId) throw new Error('No clinic');
      return partnerFailureService.resolve(clinicId, vars.id, vars.note);
    },
    onSuccess: () => {
      toast({ title: 'Resolved', description: 'Integration issue marked as resolved.' });
      queryClient.invalidateQueries({ queryKey: ['partnerFailures', clinicId] });
      queryClient.invalidateQueries({ queryKey: ['partnerFailureCounts', clinicId] });
      queryClient.invalidateQueries({ queryKey: ['partnerFailureCount', clinicId] });
      setResolveTarget(null);
      setResolutionNote('');
    },
    onError: (err: Error) => {
      toast({
        title: 'Could not resolve',
        description: err.message || 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (failureId: string) => {
      if (!clinicId) throw new Error('No clinic');
      return partnerFailureService.retry(clinicId, failureId);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['partnerFailures', clinicId] });
      queryClient.invalidateQueries({ queryKey: ['partnerFailureCounts', clinicId] });
      queryClient.invalidateQueries({ queryKey: ['partnerFailureCount', clinicId] });
      if (res?.success) {
        toast({ title: 'Retry succeeded', description: 'Issue auto-resolved.' });
      } else {
        toast({
          title: 'Retry failed',
          description: res?.error?.message || 'Please see the latest error.',
          variant: 'destructive',
        });
      }
    },
    onError: (err: Error) => {
      toast({
        title: 'Retry failed',
        description: err.message || 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const items = listData?.data?.items ?? [];
  const totalPages = listData?.data?.pagination?.total_pages ?? 1;
  const total = listData?.data?.pagination?.total ?? 0;
  const byCategory = countsData?.data?.by_category ?? {};
  const totalForFilter = countsData?.data?.count ?? 0;

  // Keep the drawer in sync with the latest list row (after retry/resolve).
  const detailItem = detailTarget
    ? items.find((it) => it.id === detailTarget.id) ?? detailTarget
    : null;

  const categoryKeys = Object.keys(PARTNER_FAILURE_CATEGORY_LABELS) as Array<
    keyof typeof PARTNER_FAILURE_CATEGORY_LABELS
  >;

  return (
    <div className="space-y-4 p-4 md:p-6">
      <Card>
        <CardHeader>
          <CardTitle>Integration Issues</CardTitle>
          <CardDescription>
            Partner-API failures that need clinic admin review. Mark as resolved once the
            underlying issue has been addressed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Tabs
              value={resolvedFilter}
              onValueChange={(v) => {
                setResolvedFilter(v as ResolvedFilter);
                setPage(1);
              }}
            >
              <TabsList>
                <TabsTrigger value="unresolved">Unresolved ({totalForFilter})</TabsTrigger>
                <TabsTrigger value="resolved">Resolved</TabsTrigger>
              </TabsList>
            </Tabs>

            <Tabs
              value={categoryFilter}
              onValueChange={(v) => {
                setCategoryFilter(v as CategoryFilter);
                setPage(1);
              }}
            >
              <TabsList>
                <TabsTrigger value="all">All ({totalForFilter})</TabsTrigger>
                {categoryKeys.map((key) => (
                  <TabsTrigger key={key} value={key}>
                    {PARTNER_FAILURE_CATEGORY_LABELS[key]} ({byCategory[key] ?? 0})
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Error</TableHead>
                  <TableHead>Related</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      No issues to show.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((row) => {
                    const link = relatedRecordLink(row);
                    const categoryLabel =
                      PARTNER_FAILURE_CATEGORY_LABELS[
                        row.category as keyof typeof PARTNER_FAILURE_CATEGORY_LABELS
                      ] ?? row.category;
                    return (
                      <TableRow
                        key={row.id}
                        className="cursor-pointer hover:bg-muted/40"
                        onClick={() => setDetailTarget(row)}
                      >
                        <TableCell>
                          <Badge variant="secondary">{categoryLabel}</Badge>
                        </TableCell>
                        <TableCell>{row.stage ?? '—'}</TableCell>
                        <TableCell className="max-w-md">
                          <div className="truncate" title={row.error_message}>
                            {row.error_message}
                          </div>
                          {row.retry_attempts > 0 && (
                            <div
                              className="mt-1 text-xs text-muted-foreground"
                              title={row.last_retry_error ?? ''}
                            >
                              {row.retry_attempts} retry{row.retry_attempts === 1 ? '' : 's'}
                              {row.last_retry_error ? ` — last: ${row.last_retry_error.slice(0, 80)}${row.last_retry_error.length > 80 ? '…' : ''}` : ''}
                            </div>
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {link ? (
                            <Link
                              to={link.to}
                              className="inline-flex items-center gap-1 text-sm text-brand-teal underline-offset-4 hover:underline"
                            >
                              {link.label}
                              <ExternalLink className="h-3 w-3" />
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {formatDate(row.created_at)}
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          {row.resolved ? (
                            <span className="text-sm text-muted-foreground">
                              Resolved {row.resolved_at ? formatDate(row.resolved_at) : ''}
                            </span>
                          ) : (
                            <div className="flex justify-end gap-2">
                              {PARTNER_FAILURE_RETRYABLE.has(row.category as never) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={retryMutation.isPending && retryMutation.variables === row.id}
                                  onClick={() => retryMutation.mutate(row.id)}
                                >
                                  {retryMutation.isPending && retryMutation.variables === row.id ? (
                                    <>
                                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                      Retrying
                                    </>
                                  ) : (
                                    <>
                                      <RefreshCw className="mr-1 h-3 w-3" />
                                      Retry
                                    </>
                                  )}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                onClick={() => {
                                  setResolveTarget(row);
                                  setResolutionNote('');
                                }}
                              >
                                Mark as resolved
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {total} {total === 1 ? 'issue' : 'issues'}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                Page {page} of {Math.max(totalPages, 1)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Sheet
        open={Boolean(detailItem)}
        onOpenChange={(open) => {
          if (!open) setDetailTarget(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full overflow-y-auto bg-white p-6 text-slate-900 sm:max-w-xl"
          style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
        >
          {detailItem && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {PARTNER_FAILURE_CATEGORY_LABELS[
                      detailItem.category as keyof typeof PARTNER_FAILURE_CATEGORY_LABELS
                    ] ?? detailItem.category}
                  </Badge>
                  {detailItem.resolved ? (
                    <Badge className="bg-emerald-100 text-emerald-700">Resolved</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700">Unresolved</Badge>
                  )}
                </SheetTitle>
                <SheetDescription>
                  {detailItem.stage ? `Stage: ${detailItem.stage} · ` : ''}
                  Occurred {formatDate(detailItem.created_at)}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                <Section title="Error">
                  <div className="whitespace-pre-wrap break-words rounded-md border bg-muted/40 p-3 text-sm">
                    {detailItem.error_message}
                  </div>
                </Section>

                {detailItem.error_payload && (
                  <Section title="Error payload">
                    <JsonBlock value={detailItem.error_payload} />
                  </Section>
                )}

                {detailItem.context && (
                  <Section title="Context">
                    <JsonBlock value={detailItem.context} />
                  </Section>
                )}

                <Section title="Details">
                  <DetailGrid>
                    <DetailRow label="Failure id" value={detailItem.id} mono />
                    <DetailRow label="Partner source" value={detailItem.partner_source ?? '—'} />
                    <DetailRow label="Clinic id" value={detailItem.clinic_id} mono />
                    <DetailRow
                      label="Patient id"
                      value={detailItem.patient_id ?? '—'}
                      mono={Boolean(detailItem.patient_id)}
                    />
                    <DetailRow
                      label="Prescription id"
                      value={detailItem.prescription_id ?? '—'}
                      mono={Boolean(detailItem.prescription_id)}
                    />
                    <DetailRow
                      label="Appointment id"
                      value={detailItem.appointment_id ?? '—'}
                      mono={Boolean(detailItem.appointment_id)}
                    />
                    <DetailRow
                      label="Practitioner id"
                      value={detailItem.practitioner_id ?? '—'}
                      mono={Boolean(detailItem.practitioner_id)}
                    />
                  </DetailGrid>
                </Section>

                {(detailItem.retry_attempts > 0 || detailItem.last_retry_at) && (
                  <Section title="Retry history">
                    <DetailGrid>
                      <DetailRow label="Attempts" value={String(detailItem.retry_attempts)} />
                      <DetailRow
                        label="Last retry"
                        value={detailItem.last_retry_at ? formatDate(detailItem.last_retry_at) : '—'}
                      />
                    </DetailGrid>
                    {detailItem.last_retry_error && (
                      <div className="mt-2 whitespace-pre-wrap break-words rounded-md border bg-muted/40 p-3 text-sm">
                        {detailItem.last_retry_error}
                      </div>
                    )}
                  </Section>
                )}

                {detailItem.resolved && (
                  <Section title="Resolution">
                    <DetailGrid>
                      <DetailRow
                        label="Resolved at"
                        value={detailItem.resolved_at ? formatDate(detailItem.resolved_at) : '—'}
                      />
                      <DetailRow
                        label="Resolved by"
                        value={
                          detailItem.resolved_by
                            ? `${detailItem.resolved_by.first_name ?? ''} ${detailItem.resolved_by.last_name ?? ''}`.trim() || detailItem.resolved_by.email
                            : '—'
                        }
                      />
                    </DetailGrid>
                    {detailItem.resolution_note && (
                      <div className="mt-2 whitespace-pre-wrap break-words rounded-md border bg-muted/40 p-3 text-sm">
                        {detailItem.resolution_note}
                      </div>
                    )}
                  </Section>
                )}

                {!detailItem.resolved && (
                  <>
                    <Separator />
                    <div className="flex flex-wrap justify-end gap-2">
                      {PARTNER_FAILURE_RETRYABLE.has(detailItem.category as never) && (
                        <Button
                          variant="outline"
                          disabled={retryMutation.isPending && retryMutation.variables === detailItem.id}
                          onClick={() => retryMutation.mutate(detailItem.id)}
                        >
                          {retryMutation.isPending && retryMutation.variables === detailItem.id ? (
                            <>
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              Retrying
                            </>
                          ) : (
                            <>
                              <RefreshCw className="mr-1 h-3 w-3" />
                              Retry
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        onClick={() => {
                          setResolveTarget(detailItem);
                          setResolutionNote('');
                        }}
                      >
                        Mark as resolved
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog
        open={Boolean(resolveTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setResolveTarget(null);
            setResolutionNote('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as resolved</DialogTitle>
            <DialogDescription>
              Optionally record what action was taken to resolve this issue.
            </DialogDescription>
          </DialogHeader>
          {resolveTarget && (
            <div className="space-y-3">
              <div className="rounded border bg-muted/40 p-3 text-sm">
                <div>
                  <strong>Category:</strong>{' '}
                  {PARTNER_FAILURE_CATEGORY_LABELS[
                    resolveTarget.category as keyof typeof PARTNER_FAILURE_CATEGORY_LABELS
                  ] ?? resolveTarget.category}
                </div>
                {resolveTarget.stage && (
                  <div>
                    <strong>Stage:</strong> {resolveTarget.stage}
                  </div>
                )}
                <div>
                  <strong>Error:</strong> {resolveTarget.error_message}
                </div>
              </div>
              <Textarea
                placeholder="Resolution note (optional)"
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                maxLength={5000}
                rows={4}
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResolveTarget(null);
                setResolutionNote('');
              }}
              disabled={resolveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!resolveTarget) return;
                resolveMutation.mutate({
                  id: resolveTarget.id,
                  note: resolutionNote.trim() || undefined,
                });
              }}
              disabled={resolveMutation.isPending}
            >
              {resolveMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resolving
                </>
              ) : (
                'Resolve'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerFailures;
